/******************************************
 *  Author : vsuthar
 *  Created On : Tue Aug 17 2021
 *  File : index.js.js
 *  ©Palo Alto Networks
 *******************************************/


import { Banner, Sheet, Body, ToggleSwitch } from '@panwds/react-ui'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  TOGGLE_CLOUD_VIEW, TOGGLE_ST_VIEW,
  USER_FLOW_MULTI,
  USER_FLOW_SINGLE
} from '../../../subscription/components/constants/SubscriptionConstant'
import { DATA_TEST_DRAWER_CLOSE } from '../../constants/TestingDataIds'
import { setUserFlow } from '../../reducers/actions/LicenseReducer.actions'
import { FF_SHOW_SCREEN_TOGGLE } from '../../utils/FeatureFlags'
import { getCommonModuleState, getVarValue } from '../../utils/Sparky'
import Page from '../page/Page'
import './Drawer.scss'
import { setSidecarOptions } from '../../reducers/actions/ViewReducer.actions'
import ManageTags from '../../../devices/components/sidecar/ManageTags'
import AssociateApps from '../../../devices/components/sidecar/AssociateApps'
import StringTemplate from '../../utils/StringTemplate'
import { LABEL_TSG_ID, LABEL_TSG_NAME } from '../../constants/LabelConstants'
import { VIEW_IAM_PROVIDERS } from '../../constants/GlobalConstants'

/**
 *
 * @param isOpen Open and Close drawer
 * @param children React Children
 * @param title Title for Drawer
 * @param subTitle
 * @param onClose close callback when click in X button
 * @param showCloseButton Boolean to show close tag
 * @param rest pass through
 * @returns {JSX.Element}
 * @constructor
 */
const Drawer = ({
  isOpen,
  tabs,
  content,
  title,
  subTitle,
  actionStatus,
  onClose = () => null,
  showCloseButton,
  sidecar = {},
  dispatch,
  ...rest
}) => {
  const { selectedTreeNode, licenseReducer } = rest
  const [viewInput, setViewInput] = useState()
  const showScreenToggle = getVarValue(FF_SHOW_SCREEN_TOGGLE, false)

  const hideTSG = getCommonModuleState()?.path === VIEW_IAM_PROVIDERS

  useEffect(() => {
    setViewInput(licenseReducer.userFlow)
  }, [licenseReducer.userFlow])

  const className = `cloud-mgm-drawer cloud-mgm-drawer-grid panwds-tw3 cm-transition-all cm-duration-300 cm-ease-in-out ${isOpen ? '-visible' : '-hide'}`
  const getActionMessage = useMemo(() => {
    return <>
      {actionStatus !== 'NONE' ?
        <Banner type="inline" appearance="standard" showIcon>
          {actionStatus}
        </Banner> : null
      }
    </>
  }, [actionStatus])

  const onSidecarClose = useCallback(() => {
    dispatch(setSidecarOptions(false))
  }, [dispatch])

  const sheetBody = useMemo(() => {
    switch (sidecar?.body) {
      case 'tags':
        return <ManageTags {...{ onSidecarClose, sidecar }} />
      case 'apps':
        return <AssociateApps {...{ onSidecarClose, sidecar }} />
      default:
        return <></>
    }
  }, [sidecar, onSidecarClose])

  return (
    <div className={className}>
      <div className={'cloud-mgm-header cm-bg-gray-100 dark:cm-bg-gray-1050'}>{getActionMessage}
        {isOpen ? (
          <>
            {/*style={{ display: sidecarOpen ? 'flex' : 'none' }}*/}
            <div id='cloud-mgm-sidecar-container' className='cm-absolute cm-flex cm-inset-0 cm-justify-end'>
              <Sheet
                size='md'
                showMask
                disablePinnedChange
                isOpen={Boolean(sidecar?.isOpen)}
                title={sidecar?.title}
                onClose={onSidecarClose}
              >
                {sheetBody}
              </Sheet>
            </div>
            <Page pageHeaderProps={{ title, onClose, showCloseButton, subTitle, style: { position: 'relative' }, ...rest?.pageHeaderProps }}>
              <div className='cm-flex cm-flex-1 cm-flex-row cm-items-end cm-bg-gray-100 dark:cm-bg-gray-1050 cm-relative'>
                {tabs}
                <div className='cm-mr-4 cm-p-2 cm-flex cm-flex-col cm-text-right' style={{ maxWidth: '380px' }}>
                  {!!selectedTreeNode?.id && !hideTSG && <>
                    <Body addClassName='cm-truncate cm-font-semibold'>
                      {StringTemplate(LABEL_TSG_NAME, [selectedTreeNode?.display_name || ''])}
                    </Body>
                    <Body addClassName='cm-truncate cm-font-semibold'>
                      {StringTemplate(LABEL_TSG_ID, [selectedTreeNode?.id || ''])}
                    </Body>
                  </>}
                  {showScreenToggle && licenseReducer.userFlow.length ? <Body addClassName='cm-mt-4 cm-mb-2 cm-truncate cm-font-semibold'>
                    <div className={'cm-flex cm-flex-row'}>
                      <Body addClassName={'cm-ml-2'}>{TOGGLE_ST_VIEW}</Body>
                      <ToggleSwitch
                        addClassName={ viewInput === USER_FLOW_MULTI ? 'cm-bg-red-600 dark:cm-bg-red-300 cm-ml-2' :
                          'cm-bg-green-600 dark:cm-bg-green-300 cm-ml-2'}
                        onClick={function noRefCheck() {
                          dispatch(setUserFlow(viewInput === USER_FLOW_MULTI ? USER_FLOW_SINGLE : USER_FLOW_MULTI))
                        }} checked={viewInput === USER_FLOW_MULTI}/>
                      <Body addClassName={'cm-ml-2'}>{TOGGLE_CLOUD_VIEW}</Body>
                    </div>
                  </Body> : <></>}
                </div>
              </div>
            </Page>
          </>
        ) : null}
      </div>
      <div data-testid={DATA_TEST_DRAWER_CLOSE}
        onClick={onClose}
        className={'cloud-mgm-closebar cm-bg-blue-steel-900 cm-opacity-30'}>
        &nbsp;
      </div>
      {isOpen ? (
        <div className='cloud-mgm-main cm-bg-white dark:cm-bg-gray-1050'>
          {content}
        </div>
      ) : null}
    </div>
  )
}

export default Drawer
